import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Badge from '@mui/material/Badge';
import { NotificationsOutlined, WorkOutline, CloseOutlined } from '@mui/icons-material';
import { updateNotificationStatus } from '../../../actions/appNotifications';
import { sortArrayOfObjectsByDate } from '../../../utils/helpers';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function NotificationBell({ variant = 'light' }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const tneList = useSelector((s) => s.appNotifications?.notifications?.TnEnotification);
  const jobsList = useSelector((s) => s.appNotifications?.notifications?.JobMatchNotification);

  const sortedJobs = sortArrayOfObjectsByDate(jobsList ?? [], 'CreatedDate');
  const sortedTnE = sortArrayOfObjectsByDate(tneList ?? [], 'CreatedDate');
  const allItems = [...sortedJobs, ...sortedTnE]
    .sort((a, b) => new Date(b.CreatedDate) - new Date(a.CreatedDate));
  const unreadCount = allItems.filter((n) => !n.Read).length;
  const preview = allItems.slice(0, 5);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleItemClick = (item) => {
    const isJob = !!item.JobAlert;
    dispatch(updateNotificationStatus(item.Id, isJob ? 'JobAlert' : 'tne'));
    setOpen(false);
    navigate(isJob ? '/dashboard/jobs' : '/dashboard/timesheets');
  };

  const iconColor = variant === 'dark' ? '#FFFFFF' : '#022D41';
  const btnBg = variant === 'dark' ? 'rgba(255,255,255,0.1)' : '#FFFFFF';
  const btnBorder = variant === 'dark' ? 'transparent' : '#E2E8ED';

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className="relative flex items-center justify-center w-10 h-10 rounded-full border cursor-pointer transition-colors"
        style={{ backgroundColor: btnBg, borderColor: btnBorder }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{ '& .MuiBadge-badge': { fontSize: '10px', minWidth: '17px', height: '17px' } }}
        >
          <NotificationsOutlined sx={{ fontSize: 20, color: iconColor }} />
        </Badge>
      </button>

      {open && (
        <>
          <style>{`
            @keyframes bell-dropdown-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
            .bell-dropdown-in { animation: bell-dropdown-in 0.15s ease both; }
          `}</style>
          <div className="bell-dropdown-in absolute right-0 top-[calc(100%+8px)] w-[320px] max-w-[88vw] bg-white rounded-[12px] border border-[#E2E8ED] shadow-[0_8px_28px_rgba(2,45,65,0.16)] z-[200] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8ED]">
              <h4 className="text-[14px] font-bold text-[#022D41] m-0">Notifications</h4>
              <button onClick={() => setOpen(false)} className="bg-transparent border-none cursor-pointer text-[#6B7A85] p-0.5">
                <CloseOutlined sx={{ fontSize: 16 }} />
              </button>
            </div>

            {preview.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2 text-[#6B7A85]">
                <NotificationsOutlined sx={{ fontSize: 26, opacity: 0.25 }} />
                <span className="text-[13px]">No notifications</span>
              </div>
            ) : (
              <div className="max-h-[320px] overflow-y-auto">
                {preview.map((item) => {
                  const isJob = !!item.JobAlert;
                  return (
                    <button
                      key={item.Id}
                      onClick={() => handleItemClick(item)}
                      className="w-full flex items-start gap-3 px-4 py-3 border-b border-[#E2E8ED] last:border-b-0 bg-transparent border-none cursor-pointer hover:bg-[#F8FAFB] transition-colors text-left"
                    >
                      <span
                        className="flex items-center justify-center w-7 h-7 rounded-full shrink-0 mt-0.5"
                        style={{
                          backgroundColor: isJob ? '#D1FAE5' : '#E6F4F8',
                          color: isJob ? '#059669' : '#0094B3',
                        }}
                      >
                        {isJob ? <WorkOutline sx={{ fontSize: 13 }} /> : <NotificationsOutlined sx={{ fontSize: 13 }} />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13.5px] leading-snug m-0 ${item.Read ? 'text-[#6B7A85]' : 'text-[#022D41] font-medium'}`}>
                          {isJob
                            ? `${item.JobAlert?.Keyword ?? 'New jobs'}: ${item.MatchingCount ?? ''} match${item.MatchingCount === 1 ? '' : 'es'}`
                            : 'Timesheet/expense update'}
                        </p>
                        <span className="text-[11.5px] text-[#9AA3AE] mt-0.5 block">{timeAgo(item.CreatedDate)}</span>
                      </div>
                      {!item.Read && <span className="w-2 h-2 rounded-full bg-[#0094B3] shrink-0 mt-2" />}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => { setOpen(false); navigate('/dashboard'); }}
              className="w-full py-2.5 text-[13px] text-[#0094B3] font-semibold bg-[#F8FAFB] border-none border-t border-[#E2E8ED] cursor-pointer hover:bg-[#F0F4F7] transition-colors"
            >
              View all
            </button>
          </div>
        </>
      )}
    </div>
  );
}